import type { ComfortCurvePoint } from '@core/presentationPrint';

interface ComfortChartProps {
  title: string;
  curve: ComfortCurvePoint[];
}

/**
 * Pure SVG comfort chart for print.
 * No Recharts dependency — renders deterministically.
 *
 * Fixed axes: X = 0–24h, Y = 0–100 (abstract comfort).
 * Markers at 8h and 24h. No temperature numbers beyond the axis.
 */
export function ComfortChart({ title, curve }: ComfortChartProps) {
  // Chart dimensions
  const W = 480;
  const H = 200;
  const PAD = { top: 24, right: 16, bottom: 32, left: 40 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  // Scales
  const xScale = (hour: number) => PAD.left + (hour / 24) * plotW;
  const yScale = (comfort: number) => PAD.top + plotH - (comfort / 100) * plotH;

  // Build SVG path
  const pathPoints = curve.map(
    (p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.hour).toFixed(1)} ${yScale(p.comfort).toFixed(1)}`
  );
  const pathD = pathPoints.join(' ');

  // Y-axis ticks
  const yTicks = [0, 25, 50, 75, 100];

  // X-axis ticks
  const xTicks = [0, 4, 8, 12, 16, 20, 24];

  // Marker hours
  const markerHours = [8, 24];

  return (
    <div className="dp-comfort-chart">
      <h4 className="dp-chart-title">{title}</h4>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="auto"
        className="dp-chart-svg"
        aria-label={title}
      >
        {/* Grid lines */}
        {yTicks.map((tick) => (
          <line
            key={`gy-${tick}`}
            x1={PAD.left}
            y1={yScale(tick)}
            x2={W - PAD.right}
            y2={yScale(tick)}
            stroke="#e2e8f0"
            strokeWidth="0.5"
          />
        ))}

        {/* 8h and 24h marker lines */}
        {markerHours.map((h) => (
          <line
            key={`marker-${h}`}
            x1={xScale(h)}
            y1={PAD.top}
            x2={xScale(h)}
            y2={PAD.top + plotH}
            stroke="#94a3b8"
            strokeWidth="1"
            strokeDasharray="4 3"
          />
        ))}

        {/* Comfort curve */}
        <path
          d={pathD}
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {curve.map((p) => (
          <circle
            key={`pt-${p.hour}`}
            cx={xScale(p.hour)}
            cy={yScale(p.comfort)}
            r="2.5"
            fill="#2563eb"
          />
        ))}

        {/* Marker labels */}
        {markerHours.map((h) => (
          <text
            key={`ml-${h}`}
            x={xScale(h)}
            y={PAD.top - 6}
            textAnchor="middle"
            fontSize="9"
            fill="#64748b"
          >
            {h}h
          </text>
        ))}

        {/* X-axis */}
        <line
          x1={PAD.left}
          y1={PAD.top + plotH}
          x2={W - PAD.right}
          y2={PAD.top + plotH}
          stroke="#94a3b8"
          strokeWidth="1"
        />
        {xTicks.map((h) => (
          <text
            key={`xt-${h}`}
            x={xScale(h)}
            y={PAD.top + plotH + 16}
            textAnchor="middle"
            fontSize="9"
            fill="#64748b"
          >
            {h}
          </text>
        ))}
        <text
          x={PAD.left + plotW / 2}
          y={H - 4}
          textAnchor="middle"
          fontSize="9"
          fill="#94a3b8"
        >
          Hours
        </text>

        {/* Y-axis */}
        <line
          x1={PAD.left}
          y1={PAD.top}
          x2={PAD.left}
          y2={PAD.top + plotH}
          stroke="#94a3b8"
          strokeWidth="1"
        />
        {yTicks.map((tick) => (
          <text
            key={`yt-${tick}`}
            x={PAD.left - 6}
            y={yScale(tick) + 3}
            textAnchor="end"
            fontSize="9"
            fill="#64748b"
          >
            {tick}
          </text>
        ))}
        <text
          x={8}
          y={PAD.top + plotH / 2}
          textAnchor="middle"
          fontSize="9"
          fill="#94a3b8"
          transform={`rotate(-90, 8, ${PAD.top + plotH / 2})`}
        >
          Comfort
        </text>
      </svg>
    </div>
  );
}
